import React, { ReactNode } from 'react'

import { Box } from '../box'
import { Button, ButtonSpinner, ButtonText } from '../button'
import { HStack } from '../hstack'
import { Icon, type IconSource } from '../icon'
import { Modal } from '../modal'
import { Pressable } from '../pressable'
import { CloseGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'
import { VStack } from '../vstack'

export type ConfirmationModalTone = 'default' | 'destructive'

// Both labels render the same outlined button to the left of confirm; they
// differ only in what the press does — `cancelLabel` dismisses via `onClose`,
// `secondaryActionLabel` runs its own `onSecondaryAction`. A union rather than
// two loose optional props so passing both is a type error instead of a
// silently-dropped label.
export type ConfirmationModalActionProps =
    | {
          cancelLabel?: string
          secondaryActionLabel?: never
          onSecondaryAction?: never
      }
    | {
          cancelLabel?: never
          secondaryActionLabel: string
          onSecondaryAction?: () => void
      }

export type ConfirmationModalProps = {
    isOpen: boolean
    /** Dialog title, rendered inside the sub-surface header band. */
    title: ReactNode
    /** Pictogram glyph. Rendered inside the spec's 62px ring — pass the icon, not the frame. */
    icon?: IconSource
    /** Escape hatch for a pictogram that isn't a ring-framed glyph. Wins over `icon`. */
    pictogram?: ReactNode
    description?: ReactNode
    confirmLabel: string
    /** Accessible name for the close control. Override to localise. */
    closeLabel?: string
    /** `destructive` fills the confirm button with `destructive/*` and a `content/knockout` label. */
    tone?: ConfirmationModalTone
    /**
     * Set while the confirm action is in flight. Disables both buttons AND blocks
     * every dismissal path (backdrop, Escape, close button), so the dialog cannot
     * be closed out from under an in-flight mutation.
     *
     * Required for any async `onConfirm`: there is no internal submit guard, so
     * without it `onConfirm` fires on every press.
     */
    isLoading?: boolean
    isDismissible?: boolean
    onConfirm: () => void
    onClose: () => void
    /** Optional form content rendered between the description and the buttons. */
    children?: ReactNode
    nativeID?: string
    testID?: string
} & ConfirmationModalActionProps

// Figma node 14231-235461, read off the bound variables and frame geometry:
// 420x324 dialog; header band 420x164 on `Background/sub-surface` with 32px
// y-padding, a 62px pictogram ring and a 12px gap to the title; content area on
// `Background/surface` with 32px x- / 36px y-padding and a 24px gap to the
// buttons; buttons are 40px tall.
//
// Note on the title token: Figma's `Size/H1` is **18** (line height 25.2), which
// is our `heading` token — NOT our `h1`, which is 2.281rem. The names collide
// across the two systems, so `size='heading'` is the faithful mapping here.
//
// The two-button variant is node 14253-235765: one row, outlined secondary left
// of the filled confirm, both content-sized (`shrink-0`) with a nowrap label —
// the design never wraps a button label. `flex-wrap` on the row is the one
// deviation, and it carries the mobile case that desktop frame doesn't cover:
// when both buttons can't share a line the row breaks and they stack, each label
// still on one line.
export const PICTOGRAM_RING =
    'h-[62px] w-[62px] items-center justify-center rounded-full border border-solid border-aos-text-default'

// Widens the dialog on narrow viewports. `size='sm'` alone is `w-[70%]`, which on
// a 360dp phone is a 252px dialog — 188px of content once the 32px x-padding is
// off, short of the 231px the widest LVP button needs (AOS-1223: "Proceed without
// the counter" is 195px of Manrope SemiBold plus 36px of borders and padding).
// 90% gives 324px/260px there, and still caps at the spec's 420px, so nothing
// moves on any viewport wide enough to reach that cap.
const DIALOG_WIDTH = 'w-[90%] max-w-[420px]'

// The spec's 40px height, as a floor rather than a fixed value. `shrink-0` keeps
// each button at its natural single-line width so the row wraps instead of
// squeezing the labels (React Native defaults to `flexShrink: 0`; web's CSS
// default of 1 is why the squeeze only showed on mobile). `max-w-full` is the
// safety net for a locale whose label is wider than the whole row: it clamps the
// button, and `min-w-0` + the label's `shrink` then wrap the text and grow the
// button rather than clipping it.
const ACTION_BUTTON = 'min-h-10 min-w-0 shrink-0 max-w-full'
const ACTION_BUTTON_LABEL = 'shrink text-center'

/**
 * Confirmation dialog per the Figma modal spec (node 14231-235461): a
 * sub-surface header band with a 62px pictogram ring + 18px title, a surface
 * content area with a centered 16px description, and 40px action buttons.
 * `tone='destructive'` fills the confirm button with `destructive/*` and a `content/knockout` label.
 */
export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
    isOpen,
    title,
    icon,
    pictogram,
    description,
    confirmLabel,
    cancelLabel,
    secondaryActionLabel,
    onSecondaryAction,
    closeLabel = 'Close',
    tone = 'default',
    isLoading = false,
    isDismissible = true,
    onConfirm,
    onClose,
    children,
    nativeID,
    testID,
}) => (
    <Modal
        isOpen={isOpen}
        onClose={onClose}
        // While the confirm action is in flight nothing may dismiss the dialog.
        // `Modal` derives both the backdrop click and Escape from `isDismissible`,
        // so gating it here is what closes those two paths; the close button below
        // is gated on the same value.
        isDismissible={isDismissible && !isLoading}
        size='sm'
        bodyClassName='p-0'
        className={`bg-aos-bg-surface ${DIALOG_WIDTH}`}
        nativeID={nativeID}
    >
        {isDismissible && (
            <Pressable
                accessibilityRole='button'
                accessibilityLabel={closeLabel}
                disabled={isLoading}
                onPress={onClose}
                className={`absolute right-4 top-4 z-10 p-1 web:hover:opacity-70 ${
                    isLoading ? 'opacity-40' : ''
                }`}
                testID={testID ? `${testID}-close` : undefined}
            >
                <CloseGlyph size={24} />
            </Pressable>
        )}
        <VStack className='w-full items-center gap-3 bg-aos-bg-sub-surface px-6 py-8'>
            {pictogram ??
                (icon ? (
                    <Box className={PICTOGRAM_RING}>
                        <Icon as={icon} size={28} className='text-aos-text-default' />
                    </Box>
                ) : null)}
            <Text size='heading' tone='default' className='text-center'>
                {title}
            </Text>
        </VStack>
        <VStack className='items-center gap-6 px-8 py-9'>
            {description ? (
                <Text size='body-lg' tone='default' className='text-center'>
                    {description}
                </Text>
            ) : null}
            {children ? <Box className='w-full'>{children}</Box> : null}
            <HStack className='w-full flex-wrap items-center justify-center gap-6'>
                {(secondaryActionLabel ?? cancelLabel) ? (
                    <Button
                        variant='secondary'
                        className={ACTION_BUTTON}
                        isDisabled={isLoading}
                        onPress={onSecondaryAction ?? onClose}
                        testID={
                            testID
                                ? `${testID}-${secondaryActionLabel ? 'secondary' : 'cancel'}`
                                : undefined
                        }
                    >
                        <ButtonText className={ACTION_BUTTON_LABEL}>
                            {secondaryActionLabel ?? cancelLabel}
                        </ButtonText>
                    </Button>
                ) : null}
                <Button
                    tone={tone}
                    className={ACTION_BUTTON}
                    isDisabled={isLoading}
                    onPress={onConfirm}
                    testID={testID ? `${testID}-confirm` : undefined}
                >
                    {isLoading ? <ButtonSpinner /> : null}
                    <ButtonText className={ACTION_BUTTON_LABEL}>{confirmLabel}</ButtonText>
                </Button>
            </HStack>
        </VStack>
    </Modal>
)

ConfirmationModal.displayName = 'ConfirmationModal'
